import React from 'react';
import { X, MapPin, Globe, Database, Tag, Clock, ExternalLink, Home, Zap, Crosshair, Navigation, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ScannerControl from '../Scanner/ScannerControl';
import ScannerResult from '../Scanner/ScannerResult';
import { exportScanToPdf } from '../../services/scanPdfExport';

const renderValue = (value) => {
  const strValue = String(value);
  if (strValue.startsWith('http://') || strValue.startsWith('https://')) {
    return (
      <a
        href={strValue}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#00CEC9',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(0, 206, 201, 0.1)',
          padding: '4px 10px',
          borderRadius: '4px',
          marginTop: '4px',
          fontSize: '0.8rem',
          border: '1px solid rgba(0, 206, 201, 0.3)',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(0, 206, 201, 0.2)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(0, 206, 201, 0.1)'; }}
      >
        Open Link <ExternalLink size={12} />
      </a>
    );
  }
  return strValue;
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{
      display: 'flex',
      gap: '12px',
      marginBottom: '12px',
      alignItems: 'flex-start',
      padding: '10px',
      backgroundColor: 'rgba(11, 61, 145, 0.1)',
      borderRadius: '6px',
      borderLeft: '3px solid #00CEC9'
    }}>
      <Icon size={16} color="#00CEC9" style={{ marginTop: '2px', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.7rem', color: '#00CEC9', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '0.95rem', color: '#FFF', fontWeight: '500', wordBreak: 'break-word', lineHeight: '1.4' }}>
          {renderValue(value)}
        </div>
      </div>
    </div>
  );
};

const OpportunityDetails = ({
  opportunity,
  onClose,
  scanRadius,
  setScanRadius,
  maxScanRadius,
  onScan,
  scanResults,
  savedScanId,
  isScanning,
  nearestItems
}) => {
  const navigate = useNavigate();
  if (!opportunity) return null;

  const isCoordPoint = opportunity._isCoordinatePoint;
  const isManual = opportunity.source === 'manual';

  // Extract tags if it's an OSM or CARTO import
  const tags = opportunity.metadata?.tags || opportunity.metadata?.carto || {};
  const hasTags = Object.keys(tags).length > 0;

  return (
    <div style={{
      background: 'rgba(7, 10, 15, 0.96)',
      backdropFilter: 'blur(12px)',
      border: `2px solid ${isCoordPoint ? '#E27B58' : '#0B3D91'}`,
      padding: '28px',
      borderRadius: '12px',
      color: '#F0F0F0',
      width: '420px',
      maxWidth: 'calc(100vw - 40px)',
      maxHeight: '85vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      boxShadow: '0 0 50px rgba(0, 0, 0, 0.9), inset 0 0 30px rgba(11, 61, 145, 0.15)',
      position: 'relative',
      zIndex: 1000
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '28px',
        borderBottom: '1px solid rgba(11, 61, 145, 0.4)',
        paddingBottom: '20px'
      }}>
        <div style={{ flex: 1, paddingRight: '15px' }}>
          <h2 style={{ 
            color: isCoordPoint ? '#E27B58' : '#E27B58', 
            fontSize: isCoordPoint ? '1.1rem' : '1.6rem', 
            margin: '0 0 10px 0', 
            lineHeight: '1.2',
            fontWeight: '900',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: 'monospace',
          }}>
            {isCoordPoint && <Navigation size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
            {opportunity.name}
          </h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {isCoordPoint ? (
              <span style={{
                fontSize: '0.75rem',
                background: 'rgba(226, 123, 88, 0.15)',
                color: '#E27B58',
                padding: '4px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(226, 123, 88, 0.5)',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                COORDINATE POINT
              </span>
            ) : (<>
            <span style={{ 
              fontSize: '0.75rem', 
              background: '#0B3D91', 
              color: 'white',
              padding: '4px 12px', 
              borderRadius: '6px',
              fontWeight: 'bold',
              textTransform: 'uppercase' 
            }}>
              {opportunity.type || 'base'}
            </span>
            <span style={{ 
              fontSize: '0.75rem', 
              background: 'rgba(0, 206, 201, 0.15)', 
              color: '#00CEC9',
              padding: '4px 12px', 
              borderRadius: '6px',
              border: '1px solid rgba(0, 206, 201, 0.4)',
              textTransform: 'uppercase',
              fontWeight: '600'
            }}>
              SOURCE: {opportunity.source}
            </span>
          </>)}
          </div>
        </div>
        <button 
          onClick={onClose} 
          style={{ 
            background: 'rgba(255,255,255,0.08)', 
            border: '1px solid rgba(255,255,255,0.1)', 
            borderRadius: '8px',
            color: '#CCC', 
            cursor: 'pointer', 
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s hover',
            outline: 'none'
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'white'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#CCC'; }}
        >
          <X size={22} />
        </button>
      </div>

      <div style={{ marginBottom: '30px' }}>
        {!isCoordPoint && <InfoRow icon={Tag} label="Brand Identity" value={opportunity.brand} />}
        <InfoRow icon={MapPin} label={isCoordPoint ? 'Coordinates' : 'Physical Address'} value={opportunity.address} />
        <InfoRow icon={Globe} label="Region / Country" value={opportunity.country} />
        <InfoRow icon={Database} label="System Category" value={tags.amenity || tags.shop} />
        {opportunity.rating && (
          <InfoRow 
            icon={Clock} 
            label="Community Rating" 
            value={`${opportunity.rating} Stars (${opportunity.userRatingsTotal || 0} reviews)`} 
          />
        )}
      </div>

      {/* Proximity Intel */}
      {nearestItems && (nearestItems.base || nearestItems.engine || nearestItems.competence) && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            fontSize: '0.85rem',
            color: '#00CEC9',
            borderBottom: '2px solid rgba(0, 206, 201, 0.3)',
            paddingBottom: '10px',
            marginBottom: '14px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            fontWeight: '900'
          }}>
            PROXIMITY INTEL
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { key: 'base', label: 'BASE', Icon: Home, color: '#4A90E2' },
              { key: 'engine', label: 'ENGINE', Icon: Zap, color: '#4CAF50' },
              { key: 'competence', label: 'COMPETENCE', Icon: Crosshair, color: '#FF5252' }
            ].map(({ key, label, Icon, color }) => {
              const item = nearestItems[key];
              if (!item) return null;
              const km = item.distance >= 1000
                ? `${(item.distance / 1000).toFixed(2)} km`
                : `${item.distance} m`;
              return (
                <div key={key} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  backgroundColor: `${color}11`,
                  border: `1px solid ${color}44`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: '6px'
                }}>
                  <Icon size={14} color={color} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.65rem', color, textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#EEE', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color, fontFamily: 'monospace', fontWeight: 'bold', flexShrink: 0 }}>{km}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginBottom: '30px' }}>
        <ScannerControl 
          radius={scanRadius} 
          setRadius={setScanRadius} 
          onScan={onScan} 
          loading={isScanning}
          maxRadius={maxScanRadius}
        />
        <ScannerResult 
          results={scanResults}
          savedScanId={savedScanId}
          onExportPDF={() => {
            const rawAllPois = Object.values(scanResults || {}).flat();
            const mockScan = {
              _id: savedScanId || ('LIVE' + Date.now()),
              center: {
                coordinates: opportunity.location?.coordinates ||
                  [opportunity.longitude || 0, opportunity.latitude || 0]
              },
              radius: scanRadius,
              scanAt: new Date(),
              results: scanResults,
              referenceId: opportunity._id
            };
            exportScanToPdf({
              scan: mockScan,
              intel: { competence: [], engines: [] },
              rawAllPois,
              identifyIntel: () => ({ isComp: false, isEng: false })
            });
          }}
        />
      </div>

      {opportunity.metadata?.notes && (
        <div style={{ 
          marginBottom: '30px', 
          padding: '20px', 
          background: 'rgba(226, 123, 88, 0.08)', 
          border: '1px solid rgba(226, 123, 88, 0.2)',
          borderRadius: '8px',
          boxShadow: 'inset 0 0 15px rgba(226, 123, 88, 0.05)'
        }}>
          <h3 style={{ 
            fontSize: '0.85rem', 
            color: '#E27B58', 
            margin: '0 0 12px 0', 
            textTransform: 'uppercase',
            fontWeight: '900',
            letterSpacing: '1px'
          }}>
            INTELLIGENCE NOTES
          </h3>
          <p style={{ fontSize: '1rem', color: '#DDD', lineHeight: '1.6', margin: 0 }}>
            {opportunity.metadata.notes}
          </p>
        </div>
      )}

      {hasTags && (
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ 
            fontSize: '0.85rem', 
            color: '#00CEC9', 
            borderBottom: '2px solid rgba(0, 206, 201, 0.3)', 
            paddingBottom: '10px', 
            marginBottom: '20px', 
            textTransform: 'uppercase',
            letterSpacing: '2px',
            fontWeight: '900'
          }}>
            TECHNICAL METADATA
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(tags).map(([key, value]) => {
              if (typeof value === 'object' || !value) return null;
              if (['name', 'brand', 'source', 'type', 'id'].includes(key.toLowerCase())) return null;
              
              return (
                <div key={key} style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  padding: '10px 14px',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <span style={{ fontSize: '0.65rem', color: '#666', textTransform: 'uppercase', marginBottom: '4px', fontWeight: 'bold' }}>{key.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: '0.85rem', color: '#EEE', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {renderValue(value)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ 
        fontSize: '0.7rem', 
        color: '#444', 
        marginTop: '40px', 
        paddingTop: '20px', 
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        fontFamily: 'monospace',
        letterSpacing: '0.5px'
      }}>
        <span>SYS_ID: {isCoordPoint ? opportunity.address : opportunity._id}</span>
        <span>{isCoordPoint ? 'LIVE POINT' : `RECORDED: ${new Date(opportunity.ingestedAt || opportunity.updatedAt).toLocaleDateString()}`}</span>
      </div>

      {/* "Ver ficha completa" button for manual opportunities */}
      {isManual && !isCoordPoint && (
        <button
          onClick={() => navigate(`/opportunities/${opportunity._id}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            background: 'linear-gradient(135deg, rgba(226,123,88,0.15), rgba(226,123,88,0.05))',
            border: '1px solid rgba(226,123,88,0.5)',
            borderRadius: '8px',
            color: '#E27B58',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(226,123,88,0.25)'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(226,123,88,0.15), rgba(226,123,88,0.05))'; }}
        >
          <Target size={16} />
          Ver ficha completa →
        </button>
      )}
    </div>
  );
};

export default OpportunityDetails;
